import z from 'zod/v4';
import type * as z4 from 'zod/v4/core';
import { DefaultPresetDataSchema } from './default-preset-schema';
import type { OmitDeep, Paths } from 'type-fest';
import { omit } from 'lodash';

export const stringToJSONSchema = z.string().transform((str, ctx) => {
  try {
    return JSON.parse(str);
  } catch {
    ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
    return z.NEVER;
  }
});

export function typedOmit<
  TData extends Parameters<typeof omit>[0],
  TPaths extends Paths<TData>[],
>(data: TData, path: TPaths) {
  return omit(data, path) as OmitDeep<TData, TPaths[number]>;
}

export function createPresetSchema<
  T extends z4.$ZodType<unknown, typeof z4.JSONSchema>,
  TVersion extends string,
>(version: TVersion, schema: T) {
  return z.object({
    ...DefaultPresetDataSchema.shape,
    version: z.literal(version),
    presetData: schema,
  });
}

export function createMigration<
  TFrom extends z4.$ZodType<unknown, typeof z4.JSONSchema>,
  TTo extends z4.$ZodType<unknown, typeof z4.JSONSchema>,
  TVersion extends string,
>(
  from: TFrom,
  to: TTo,
  migrationConfig: {
    fromVersion: TVersion;
    migrator: (data: z4.output<TFrom>) => z4.output<TTo>;
  }
) {
  function runner(data: any) {
    const oldData = z.parse(from, data);

    const migratedData = migrationConfig.migrator(oldData);

    return z.parse(to, migratedData);
  }

  return { fromVersion: migrationConfig.fromVersion, migrator: runner };
}
